/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useEffect, useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Formik, Field } from 'formik'
import * as Yup from 'yup'

import { MaintenanceEntity } from 'utils/__interfaces/SuperAdministration/Maintenance'
import MAINTENANCE_TYPE from 'utils/__enums/SuperAdministration/Maintenance'
import { DESCRIPTION_CHARACTER_LIMIT } from 'utils/__consts/SuperAdministration/Maintenances'
import {
  checkMaintenances,
  submitDegradedDemand,
} from 'services/maintenance-service'

/** Components */
import Form from 'components/Forms/Form'
import SvgIcon from 'components/Icons/SvgIcon'
import Loading from 'components/Loaders/Loading'
import TextArea from 'components/Textes/TextArea'
import FormLabel from 'components/Forms/FormLabel'
import FormInput from 'components/Forms/FormInput'
import FormSubmit from 'components/Forms/FormSubmit'
import TurnstileCaptcha from 'components/Captcha/TurnstileCaptcha'
import type { TurnstileInstance } from '@marsidev/react-turnstile'
import FormInputErrorMessage from 'components/Forms/FormInputErrorMessage'
import PianoGTM from 'utils/Common/pianoAnalytics-utils'

interface PreviewProps {
  content: string | null
  displayForm?: boolean
  templateType?: number
  maintenanceType: string
  isPreview: boolean
}

declare global {
  interface Window {
    previewProps?: PreviewProps
    previewWindowRef?: Window | null
  }
}

interface Props {
  maintenanceType?: string
}

const MaintenanceContent: FC<Props> = ({ maintenanceType }: Props) => {
  const { t } = useTranslation()
  const location = useLocation()
  const turnstileRef = useRef<TurnstileInstance | null>()
  const fetchAttempted = useRef(false)

  const [maintenanceInfos, setMaintenanceInfos] = useState<
    Partial<MaintenanceEntity>
  >()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingForm, setIsLoadingForm] = useState(false)
  const [loadingTurnstile, setLoadingTurnstile] = useState(true)
  const [apiResponse, setApiResponse] = useState<number | null>(null)
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false)

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    address: '',
    cpCode: '',
    city: '',
    description: '',
  }

  const validationSchema = Yup.object({
    firstName: Yup.string().required(t('form.errorMessage.required')),
    lastName: Yup.string().required(t('form.errorMessage.required')),
    email: Yup.string()
      .email(t('form.errorMessage.emailNotValid'))
      .required(t('form.errorMessage.required')),
    company: Yup.string().required(t('form.errorMessage.required')),
    address: Yup.string().required(t('form.errorMessage.required')),
    cpCode: Yup.string()
      .matches(/^\d{5}$/, t('form.errorMessage.fiveDegits'))
      .required(t('form.errorMessage.required')),
    city: Yup.string().required(t('form.errorMessage.required')),
    description: Yup.string().required(t('form.errorMessage.required')),
  })

  const handleSubmit = async (
    values: any,
    { resetForm }: { resetForm: () => void }
  ) => {
    try {
      setApiResponse(null)
      setIsLoadingForm(true)
      const result = await submitDegradedDemand(
        values,
        turnstileRef.current?.getResponse() ?? ''
      )
      resetForm()
      setApiResponse(result)
      // Data layers GTM track document
      PianoGTM('Création demande dégradé')
    } catch (error) {
      turnstileRef.current?.reset()
    } finally {
      setIsLoadingForm(false)
    }
  }

  const isInPreviewMode = () => {
    const searchParams = new URLSearchParams(location.search)
    return searchParams.has('preview')
  }

  useEffect(() => {
    const handlePreviewPropsReady = () => {
      if (window.previewProps) {
        const { content, displayForm, maintenanceType } = window.previewProps
        setMaintenanceInfos({
          message: content,
          withForm: displayForm,
          type: maintenanceType as any,
        })
      }
    }

    const inPreviewMode = isInPreviewMode()
    setIsPreviewMode(inPreviewMode)

    if (inPreviewMode) {
      if (window.previewProps) {
        handlePreviewPropsReady()
        return undefined
      }

      const storedProps = sessionStorage.getItem('maintenancePreviewProps')
      if (storedProps) {
        try {
          window.previewProps = JSON.parse(storedProps)
          handlePreviewPropsReady()
        } catch (e) {
          // catch error props here
        }
      }

      window.addEventListener('previewPropsReady', handlePreviewPropsReady)

      return () => {
        window.removeEventListener('previewPropsReady', handlePreviewPropsReady)
      }
    }

    if (!fetchAttempted.current) {
      const getMaintenanceInfos = async () => {
        try {
          setIsLoading(true)
          const apiResponse = await checkMaintenances(maintenanceType)
          if (apiResponse && apiResponse.length > 0) {
            setMaintenanceInfos(apiResponse[0])
          }
        } catch (error) {
          // catch error api here
        } finally {
          setIsLoading(false)
          fetchAttempted.current = true
        }
      }
      getMaintenanceInfos()
    }

    return undefined
  }, [location.search])

  return (
    <div className="maintenance_page errorbody">
      <div className="errorbody_text-container">
        <div className="content">
          <h2 className="title">{t('error.maintenanceError.all.title')}</h2>
          <Loading isLoading={isLoading && !isPreviewMode}>
            {/* eslint-disable react/no-danger */}
            <div
              className="message"
              dangerouslySetInnerHTML={{
                __html:
                  maintenanceInfos?.message ??
                  t('superadminpage.maintenance.softText.withoutForm'),
              }}
            />
            {/* eslint-enable react/no-danger */}
            {maintenanceInfos?.withForm && (
              <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={validationSchema}
              >
                {({ values, setFieldValue, dirty, isValid }) => (
                  <Form className="form overflow-hidden row">
                    <div className="col-md-6">
                      <FormLabel mandatory htmlFor="firstName">
                        {t('superadminpage.maintenance.form.label.firstName')}
                      </FormLabel>
                      <FormInput
                        name="firstName"
                        type="text"
                        placeholder={t(
                          'superadminpage.maintenance.form.label.firstName'
                        )}
                      />
                      <FormInputErrorMessage name="firstName" />
                    </div>
                    <div className="col-md-6">
                      <FormLabel mandatory htmlFor="lastName">
                        {t('superadminpage.maintenance.form.label.lastName')}
                      </FormLabel>
                      <FormInput
                        name="lastName"
                        type="text"
                        placeholder={t(
                          'superadminpage.maintenance.form.label.lastName'
                        )}
                      />
                      <FormInputErrorMessage name="lastName" />
                    </div>
                    <div className="col-md-12">
                      <FormLabel mandatory htmlFor="email">
                        {t('superadminpage.maintenance.form.label.email')}
                      </FormLabel>
                      <FormInput
                        name="email"
                        type="text"
                        placeholder={t(
                          'superadminpage.maintenance.form.placeholder.email'
                        )}
                      />
                      <FormInputErrorMessage name="email" />
                    </div>
                    <div className="col-md-12">
                      <FormLabel mandatory htmlFor="company">
                        {t('superadminpage.maintenance.form.label.company')}
                      </FormLabel>
                      <FormInput
                        name="company"
                        type="text"
                        placeholder={t(
                          'superadminpage.maintenance.form.placeholder.company'
                        )}
                      />
                      <FormInputErrorMessage name="company" />
                    </div>

                    <div className="col-md-12">
                      <FormLabel mandatory htmlFor="address">
                        {t('superadminpage.maintenance.form.label.address')}
                      </FormLabel>
                      <FormInput
                        name="address"
                        type="text"
                        placeholder={t(
                          'superadminpage.maintenance.form.label.address'
                        )}
                      />
                      <FormInputErrorMessage name="address" />
                    </div>

                    <div className="col-3">
                      <FormLabel mandatory htmlFor="cpCode">
                        {t('superadminpage.maintenance.form.label.cpCode')}
                      </FormLabel>
                      <Field
                        name="cpCode"
                        type="string"
                        placeholder={t(
                          'superadminpage.maintenance.form.placeholder.cpCode'
                        )}
                        className="form_input custom-input"
                        onChange={(e: any) => {
                          if (/^[-]{0}\d{0,5}$/.test(e.target.value)) {
                            setFieldValue('cpCode', e.target.value)
                          }
                        }}
                      />
                      <FormInputErrorMessage name="cpCode" />
                    </div>
                    <div className="col-9">
                      <FormLabel mandatory htmlFor="city">
                        {t('superadminpage.maintenance.form.label.city')}
                      </FormLabel>
                      <FormInput
                        type="text"
                        name="city"
                        placeholder={t(
                          'superadminpage.maintenance.form.label.city'
                        )}
                      />
                      <FormInputErrorMessage name="city" />
                    </div>

                    <div className="col-12">
                      <FormLabel mandatory htmlFor="description">
                        {t('superadminpage.maintenance.form.label.description')}
                      </FormLabel>
                      <TextArea
                        title=""
                        placeholder={t(
                          'superadminpage.maintenance.form.placeholder.description'
                        )}
                        initialValue=""
                        currentValue={values.description}
                        handleCurrentValueChange={(value) =>
                          setFieldValue('description', value)
                        }
                        ResetButton={false}
                        className="p-0"
                        caracterelimit={DESCRIPTION_CHARACTER_LIMIT}
                        showLimitCounter
                        showLimitMessage={true.valueOf()}
                      />
                      <FormInputErrorMessage name="description" />
                    </div>
                    <div className="col-12 mb-3">
                      <TurnstileCaptcha
                        turnstuleRef={turnstileRef}
                        setLoadingTurnstile={setLoadingTurnstile}
                      />
                    </div>

                    {apiResponse && (
                      <div className="col-12 mb-3">
                        {apiResponse === 200
                          ? t('superadminpage.maintenance.form.success')
                          : t('superadminpage.maintenance.form.error')}
                      </div>
                    )}

                    <div className="mx-auto">
                      <FormSubmit
                        disabled={
                          !isValid ||
                          !dirty ||
                          loadingTurnstile ||
                          isLoadingForm ||
                          isPreviewMode
                        }
                      >
                        <Loading isLoading={isLoadingForm} inButton>
                          <SvgIcon name="check" />
                          {t('superadminpage.maintenance.form.submit')}
                        </Loading>
                      </FormSubmit>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </Loading>
        </div>
      </div>
    </div>
  )
}

MaintenanceContent.defaultProps = {
  maintenanceType: MAINTENANCE_TYPE.ALL,
}

export default MaintenanceContent
